import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Megaphone } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

interface BannerItem {
  id: number;
  text: string;
  link?: string | null;
  isActive: boolean;
  order: number;
}

interface MobilePlatformAdTickerProps {
  className?: string;
}

const MobilePlatformAdTicker: React.FC<MobilePlatformAdTickerProps> = ({ className = '' }) => {
  const [items, setItems] = useState<BannerItem[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/banners`)
      .then((res) => {
        if (cancelled) return;
        const active = (res.data.banners || [])
          .filter((b: BannerItem) => b.isActive)
          .sort((a: BannerItem, b: BannerItem) => a.order - b.order);
        setItems(active);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (items.length === 0) return null;

  const handleItemClick = (item: BannerItem) => {
    if (item.link) {
      if (item.link.startsWith('http')) {
        globalThis.open(item.link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.link);
      }
    } else {
      navigate('/announcement-details');
    }
  };

  return (
    <div
      className={`flex h-8 w-full items-center rounded-none sm:rounded-md border-y sm:border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-100 shadow-sm dark:border-amber-500/30 dark:from-amber-950/40 dark:to-orange-900/30 overflow-hidden whitespace-nowrap ${className}`}
      aria-live="polite"
    >
      <div className="flex items-center animate-marquee hover:[animation-play-state:paused] min-w-max">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleItemClick(item)}
            className="flex items-center gap-1.5 px-6 text-start transition-all duration-200 active:scale-95 outline-none"
            title={item.text}
          >
            <Megaphone className="h-3.5 w-3.5 shrink-0 text-orange-500 animate-pulse dark:text-orange-400" aria-hidden />
            <span className="text-[10px] font-bold leading-tight text-amber-900 dark:text-amber-100">
              {item.text}
            </span>
          </button>
        ))}
        {/* Duplicate items for seamless loop */}
        {items.length > 0 && items.map((item) => (
          <button
            key={`dup-${item.id}`}
            type="button"
            onClick={() => handleItemClick(item)}
            className="flex items-center gap-1.5 px-6 text-start transition-all duration-200 active:scale-95 outline-none"
            title={item.text}
          >
            <Megaphone className="h-3.5 w-3.5 shrink-0 text-orange-500 animate-pulse dark:text-orange-400" aria-hidden />
            <span className="text-[10px] font-bold leading-tight text-amber-900 dark:text-amber-100">
              {item.text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MobilePlatformAdTicker;
